import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  FormControlLabel, 
  Switch, 
  Paper,
  Typography,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { Category } from '../services/DashboardsService';

interface CategoryFormProps {
  initialValues: Partial<Category>;
  onSave: (category: any) => Promise<boolean>;
  onCancel: () => void;
  isNew?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ 
  initialValues, 
  onSave, 
  onCancel,
  isNew = false
}) => {
  const [formData, setFormData] = useState<Partial<Category>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }
    
    if (formData.orderBy === undefined || formData.orderBy === null) {
      newErrors.orderBy = 'Order is required';
    } else if (isNaN(Number(formData.orderBy))) {
      newErrors.orderBy = 'Order must be a number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Ensure orderBy is a number
      const dataToSave = {
        ...formData,
        orderBy: Number(formData.orderBy)
      };
      
      const success = await onSave(dataToSave);
      if (!success) {
        setError('Failed to save category. Please try again.');
      }
    } catch (err) {
      console.error('Error saving category:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0' }}>
      <Typography variant="h6" component="h3" gutterBottom>
        {isNew ? 'Create New Category' : 'Edit Category'}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="name"
          label="Name"
          name="name"
          value={formData.name || ''}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name || ''}
          autoFocus
        />
        
        <TextField
          margin="normal"
          fullWidth
          id="shortname"
          label="Short Name"
          name="shortname"
          value={formData.shortname || ''}
          onChange={handleChange}
        />
        
        <TextField
          margin="normal"
          fullWidth
          id="subtitle"
          label="Subtitle"
          name="subtitle"
          value={formData.subtitle || ''}
          onChange={handleChange}
        />
        
        <TextField
          margin="normal"
          required
          fullWidth
          id="orderBy"
          label="Order"
          name="orderBy"
          type="number"
          value={formData.orderBy === undefined ? '' : formData.orderBy}
          onChange={handleChange}
          error={!!errors.orderBy}
          helperText={errors.orderBy || ''}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={!!formData.deactivated}
              onChange={handleChange}
              name="deactivated"
              color="primary"
            />
          }
          label="Deactivated"
          sx={{ mt: 2 }}
        />
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default CategoryForm;